import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import shellLogo from '../../assets/images/logo1.png';

/**
 * Footer Component - Modern Warm Heritage Design
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (newsletterEmail) {
      setSubscribed(true);
      setNewsletterEmail('');
      setTimeout(() => setSubscribed(false), 5000);
    }
  };

  return (
    <footer className="bg-[#F5CE9F] text-[#2E1F14] pt-6 pb-3.5 border-t border-[#E5BD8E]">
      <div className="container mx-auto px-6 sm:px-8 lg:px-12">
        {/* Main 4-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 pb-5">
          
          {/* Column 1: Brand & Newsletter */}
          <div className="flex flex-col justify-between">
            {/* Logo & Tagline */}
            <div>
              <div className="flex flex-col items-start">
                <img src={shellLogo} alt="Vāstusindhu Conch" className="w-8 h-auto mb-1.5" />
                <span className="text-lg font-serif tracking-[0.2em] text-[#2D1B0D] [font-variant:small-caps]">
                  Vāstusindhu
                </span>
                <p className="font-serif italic text-xs text-[#4D331A] mt-1 leading-snug">
                  Time-honoured wisdom of<br />authentic Vastu Shastra
                </p>
              </div>
            </div>

            {/* Newsletter Form */}
            <div className="mt-4">
              <h4 className="font-serif [font-variant:small-caps] tracking-[0.18em] text-xs font-semibold text-[#964B1A] mb-1.5">
                Quarterly Vastu Newsletter
              </h4>
              <form onSubmit={handleNewsletterSubmit} className="flex items-center gap-2 max-w-sm">
                <input
                  type="email"
                  placeholder="Your email address"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="w-full bg-white font-sans text-gray-800 placeholder-gray-500 text-xs px-3 py-1.5 rounded border border-[#D5A773] focus:outline-none focus:border-[#964B1A] shadow-sm"
                  required
                />
                <button
                  type="submit"
                  className="bg-[#A6521A] hover:bg-[#8A3E0F] text-white font-sans text-xs font-semibold px-3.5 py-1.5 rounded transition-colors duration-200 flex-shrink-0 shadow-sm"
                >
                  Subscribe
                </button>
              </form>
              {subscribed ? (
                <p className="font-sans text-[11px] text-green-800 mt-1 font-medium">Thank you for subscribing!</p>
              ) : (
                <a
                  href="#newsletter-archive"
                  onClick={(e) => e.preventDefault()}
                  className="font-sans text-[11px] text-[#8A461A] hover:underline mt-1 inline-block transition-colors"
                >
                  Read past issues
                </a>
              )}
            </div>
          </div>

          {/* Column 2: Services & Learn */}
          <div className="flex flex-col justify-between space-y-4">
            {/* Services */}
            <div>
              <h4 className="font-serif [font-variant:small-caps] tracking-[0.18em] text-sm font-semibold text-[#964B1A] mb-1.5">
                Services
              </h4>
              <ul className="space-y-1 font-sans text-xs sm:text-[13px] text-[#2E1F14]">
                <li>
                  <Link to="/our-services/online-consultation" className="hover:text-[#964B1A] transition-colors block">
                    Online Review of Architectural Plans
                  </Link>
                </li>
                <li>
                  <Link to="/online-vastu-self-assessment" className="hover:text-[#964B1A] transition-colors block">
                    Online Vastu Self-Assessment
                  </Link>
                </li>
                <li>
                  <Link to="/our-services/online-consultation" className="hover:text-[#964B1A] transition-colors block">
                    Schedule an Online Consultation
                  </Link>
                </li>
                <li>
                  <Link to="/our-services" className="hover:text-[#964B1A] transition-colors block">
                    Onsite Consultation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Learn */}
            <div>
              <h4 className="font-serif [font-variant:small-caps] tracking-[0.18em] text-sm font-semibold text-[#964B1A] mb-1.5">
                Learn
              </h4>
              <ul className="space-y-1 font-sans text-xs sm:text-[13px] text-[#2E1F14]">
                <li>
                  <Link to="/vastu-knowledge/advance-topics" className="hover:text-[#964B1A] transition-colors block">
                    Vastu in Depth
                  </Link>
                </li>
                <li>
                  <Link to="/vastu-dosh-symptoms" className="hover:text-[#964B1A] transition-colors block">
                    Symptoms of Harmful Vastu Energies
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Column 3: Vastu For */}
          <div>
            <h4 className="font-serif [font-variant:small-caps] tracking-[0.18em] text-sm font-semibold text-[#964B1A] mb-1.5">
              Vastu For
            </h4>
            <ul className="space-y-1 font-sans text-xs sm:text-[13px] text-[#2E1F14]">
              <li>
                <Link to="/vastu-knowledge/advance-topics?tab=land" className="hover:text-[#964B1A] transition-colors block">
                  Vastu for Land
                </Link>
              </li>
              <li>
                <Link to="/vastu-knowledge/advance-topics?tab=homes" className="hover:text-[#964B1A] transition-colors block">
                  Vastu for Homes
                </Link>
              </li>
              <li>
                <Link to="/vastu-knowledge/advance-topics?tab=offices" className="hover:text-[#964B1A] transition-colors block">
                  Vastu for Offices
                </Link>
              </li>
              <li>
                <Link to="/vastu-knowledge/advance-topics?tab=factories" className="hover:text-[#964B1A] transition-colors block">
                  Vastu for Factories
                </Link>
              </li>
              <li>
                <Link to="/vastu-knowledge/advance-topics?tab=relationships" className="hover:text-[#964B1A] transition-colors block">
                  Vastu for Relationships
                </Link>
              </li>
              <li>
                <Link to="/vastu-knowledge/advance-topics?tab=wealth" className="hover:text-[#964B1A] transition-colors block">
                  Vastu for Wealth
                </Link>
              </li>
              <li>
                <Link to="/vastu-knowledge/advance-topics?tab=mental" className="hover:text-[#964B1A] transition-colors block">
                  Vastu for Mental Peace
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact & Socials */}
          <div>
            <h4 className="font-serif [font-variant:small-caps] tracking-[0.18em] text-sm font-semibold text-[#964B1A] mb-1.5">
              Contact
            </h4>
            <div className="space-y-0.5 font-sans text-xs sm:text-[13px] text-[#2E1F14]">
              <p className="font-bold text-[#1F140E]">Arun Vyas - Vāstusindhu</p>
              <p>Sri Aurobindo Bhavan</p>
              <p>C-56/36, Sector 62</p>
              <p>Noida 201309, Uttar Pradesh, India</p>
            </div>

            {/* Direct Contact links */}
            <div className="mt-2.5 space-y-1 font-sans text-xs sm:text-[13px] text-[#2E1F14]">
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-[#964B1A] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href="tel:+919891422794" className="hover:text-[#964B1A] transition-colors">
                  +91 98914 22794
                </a>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-[#964B1A] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:arunvyas@vastusindhu.com" className="hover:text-[#964B1A] transition-colors">
                  arunvyas@vastusindhu.com
                </a>
              </div>
            </div>

            {/* Social Media Tiles */}
            <div className="flex items-center gap-2 mt-3.5">
              {/* Facebook */}
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-7 h-7 sm:w-8 sm:h-8 rounded bg-[#3b5998] flex items-center justify-center text-white hover:opacity-90 transition-opacity shadow-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>

              {/* Instagram */}
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-7 h-7 sm:w-8 sm:h-8 rounded bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] flex items-center justify-center text-white hover:opacity-90 transition-opacity shadow-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>

              {/* YouTube */}
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="w-7 h-7 sm:w-8 sm:h-8 rounded bg-[#ff0000] flex items-center justify-center text-white hover:opacity-90 transition-opacity shadow-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>

              {/* Pinterest */}
              <a
                href="https://pinterest.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Pinterest"
                className="w-7 h-7 sm:w-8 sm:h-8 rounded bg-[#bd081c] flex items-center justify-center text-white hover:opacity-90 transition-opacity shadow-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.627 0-12 5.372-12 12 0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345-.09.375-.291 1.199-.332 1.357-.053.211-.174.256-.402.155-1.499-.696-2.435-2.883-2.435-4.64 0-3.777 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.623 0 12-5.375 12-12 0-6.623-5.377-12-12-12z" />
                </svg>
              </a>

              {/* LinkedIn */}
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-7 h-7 sm:w-8 sm:h-8 rounded bg-[#0077b5] flex items-center justify-center text-white hover:opacity-90 transition-opacity shadow-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>

              {/* WhatsApp */}
              <a
                href="https://wa.me/919891422794"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="w-7 h-7 sm:w-8 sm:h-8 rounded bg-[#25d366] flex items-center justify-center text-white hover:opacity-90 transition-opacity shadow-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
              </a>
            </div>
          </div>

        </div>

        {/* Bottom Bar Divider & Legal */}
        <div className="border-t border-[#DFB685] pt-3 flex flex-col sm:flex-row items-center justify-between font-sans text-xs text-[#4A382A] gap-2">
          <p>© {currentYear} Vāstusindhu. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-6 text-[#4A382A]">
            <a href="#privacy" onClick={(e) => e.preventDefault()} className="hover:text-[#964B1A] transition-colors">
              Privacy Policy
            </a>
            <a href="#terms" onClick={(e) => e.preventDefault()} className="hover:text-[#964B1A] transition-colors">
              Terms of Use
            </a>
            <a href="#refund" onClick={(e) => e.preventDefault()} className="hover:text-[#964B1A] transition-colors">
              Refund and Cancellation Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
